// Seller Users screen — real owner/staff list backed by /seller/team.
// Owner invites by email; staff see the list read-only.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import {
  SellerTeamApiError,
  fetchSellerTeam,
  inviteStaff,
  removeStaff,
  resendInvite,
  revokeInvite,
  setStaffPricesHidden,
  type SellerTeamRow,
} from '../../api/seller-team'
import { formatRelative } from '../../lib/datetime'
import { useToastStore } from '../../lib/toast'
import { useT } from '../../i18n'
import { sellerTeamI18n } from '../../i18n/settings/seller-team'
import { DataTableShell } from '../../components/ui/data-table-shell'

const QUERY_KEY = ['seller', 'team']

function initials(label: string): string {
  const parts = label.split(/[\s@.]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase()
}

export function SellerTeam() {
  const t = useT(sellerTeamI18n)
  const queryClient = useQueryClient()
  const push = useToastStore((s) => s.push)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [hidePrices, setHidePrices] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<SellerTeamRow | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSellerTeam,
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  const notify = (title: string) => push({ variant: 'success', title, duration_ms: 3000 })
  const warnEmail = () =>
    push({ variant: 'warning', title: t.toasts.emailFailed, duration_ms: 6000 })
  const fail = () => push({ variant: 'error', title: t.errors.generic, duration_ms: 4000 })

  const inviteMutation = useMutation({
    mutationFn: inviteStaff,
    onSuccess: (row) => {
      setInviteOpen(false)
      setEmail('')
      setHidePrices(false)
      setInviteError(null)
      if (row.email_sent === false) warnEmail()
      else notify(t.toasts.invited)
      void refresh()
    },
    onError: (err) => {
      const code = err instanceof SellerTeamApiError ? err.code : undefined
      setInviteError(
        code === 'email_already_registered'
          ? t.errors.emailRegistered
          : code === 'invitation_pending'
            ? t.errors.pending
            : t.errors.generic,
      )
    },
  })
  const resendMutation = useMutation({
    mutationFn: resendInvite,
    onSuccess: (row) => {
      if (row.email_sent === false) warnEmail()
      else notify(t.toasts.resent)
      void refresh()
    },
    onError: fail,
  })
  const revokeMutation = useMutation({
    mutationFn: revokeInvite,
    onSuccess: () => {
      notify(t.toasts.revoked)
      void refresh()
    },
    onError: fail,
  })
  const pricesMutation = useMutation({
    mutationFn: setStaffPricesHidden,
    onSuccess: (_void, vars) => {
      notify(vars.hidden ? t.toasts.pricesHidden : t.toasts.pricesShown)
      void refresh()
    },
    onError: fail,
  })
  const removeMutation = useMutation({
    mutationFn: removeStaff,
    onSuccess: () => {
      setRemoveTarget(null)
      notify(t.toasts.removed)
      void refresh()
    },
    onError: () => {
      setRemoveTarget(null)
      fail()
    },
  })

  const notASeller =
    error instanceof SellerTeamApiError && error.status === 403 && error.code === 'not_a_seller'
  const rows = data?.rows ?? []
  const canManage = data?.can_manage ?? false

  const openInvite = () => {
    setEmail('')
    setHidePrices(false)
    setInviteError(null)
    setInviteOpen(true)
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.title}</h2>
        {canManage && (
          <Button variant="primary" size="sm" onClick={openInvite}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t.invite}
          </Button>
        )}
      </div>

      <div className="mt-5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {isLoading && (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
        )}
        {notASeller && (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.notAvailable}</div>
        )}
        {error && !notASeller && (
          <div className="p-6 text-sm text-rose-700">{t.loadFailed}</div>
        )}
        {data && rows.length === 0 && (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.empty}</div>
        )}
        {data && rows.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.name}</th>
                <th>{t.columns.email}</th>
                <th>{t.columns.role}</th>
                <th>{t.columns.status}</th>
                <th>{t.columns.prices}</th>
                {canManage && (
                  <th className="text-right">{t.columns.actions}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const label = r.display_name ?? r.email
                return (
                  <tr key={`${r.kind}-${r.id}`}>
                    <td className="py-2 text-[var(--color-text)] font-medium">
                      <div className="flex items-center gap-3">
                        <span
                          aria-hidden="true"
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-xs font-semibold text-[var(--color-text-muted)]"
                        >
                          {initials(label)}
                        </span>
                        {label}
                      </div>
                    </td>
                    <td className="py-2 text-[var(--color-text-muted)]">{r.email}</td>
                    <td className="py-2">
                      <Badge variant="primary">{t.roles[r.role]}</Badge>
                    </td>
                    <td className="py-2">
                      <Badge variant={r.status === 'active' ? 'success' : 'warning'}>
                        {t.status[r.status]}
                      </Badge>
                      {r.status === 'invited' && r.invited_at && (
                        <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                          {t.invitedAgo(formatRelative(r.invited_at))}
                        </div>
                      )}
                    </td>
                    <td className="py-2">
                      {canManage && r.kind === 'member' && r.role === 'staff' && !r.prices_locked ? (
                        <PricesSwitch
                          visible={!r.prices_hidden}
                          pending={pricesMutation.isPending && pricesMutation.variables?.id === r.id}
                          label={r.prices_hidden ? t.prices.show(label) : t.prices.hide(label)}
                          text={
                            pricesMutation.isPending && pricesMutation.variables?.id === r.id
                              ? t.prices.updating
                              : r.prices_hidden
                                ? t.prices.hidden
                                : t.prices.visible
                          }
                          onToggle={() =>
                            pricesMutation.mutate({ id: r.id, hidden: !r.prices_hidden })
                          }
                        />
                      ) : (
                        <Badge variant={r.prices_hidden ? 'warning' : 'neutral'}>
                          {r.prices_locked
                            ? t.prices.lockedByPexify
                            : r.prices_hidden
                              ? t.prices.hidden
                              : t.prices.visible}
                        </Badge>
                      )}
                    </td>
                    {canManage && (
                      <td className="py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.kind === 'invitation' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={resendMutation.isPending}
                                onClick={() => resendMutation.mutate(r.id)}
                              >
                                {t.actions.resend}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={revokeMutation.isPending}
                                onClick={() => revokeMutation.mutate(r.id)}
                              >
                                {t.actions.revoke}
                              </Button>
                            </>
                          )}
                          {r.kind === 'member' && r.role === 'staff' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRemoveTarget(r)}
                            >
                              {t.actions.remove}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      {data && !canManage && (
        <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t.staffNote}</p>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.modal.title}</DialogTitle>
            <DialogDescription>{t.modal.roleNote}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (!email.trim()) return
              setInviteError(null)
              inviteMutation.mutate({ email: email.trim(), hidePrices })
            }}
          >
            <div>
              <label
                htmlFor="seller-team-invite-email"
                className="block text-xs font-medium text-[var(--color-text)] mb-1"
              >
                {t.modal.email}
              </label>
              <input
                id="seller-team-invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm"
                placeholder="user@example.com"
                maxLength={254}
                autoComplete="off"
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-[var(--color-text)]">
              <input
                type="checkbox"
                checked={hidePrices}
                onChange={(e) => setHidePrices(e.target.checked)}
                className="mt-0.5 h-4 w-4"
                aria-describedby="seller-team-invite-hide-prices-note"
              />
              <span>
                <span className="font-medium">{t.modal.hidePrices}</span>
                <span
                  id="seller-team-invite-hide-prices-note"
                  className="block text-xs text-[var(--color-text-muted)]"
                >
                  {t.modal.hidePricesNote}
                </span>
              </span>
            </label>
            {inviteError && (
              <div className="rounded-md border-l-4 border-l-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {inviteError}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setInviteOpen(false)}
                disabled={inviteMutation.isPending}
              >
                {t.modal.cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={inviteMutation.isPending || !email.trim()}
              >
                {inviteMutation.isPending ? t.modal.sending : t.modal.send}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.removeConfirm.title}</DialogTitle>
            <DialogDescription>
              {t.removeConfirm.body(removeTarget?.display_name ?? removeTarget?.email ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRemoveTarget(null)}
              disabled={removeMutation.isPending}
            >
              {t.modal.cancel}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={removeMutation.isPending}
              onClick={() => removeTarget && removeMutation.mutate(removeTarget.id)}
            >
              {t.removeConfirm.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PricesSwitch({
  visible,
  pending,
  label,
  text,
  onToggle,
}: {
  visible: boolean
  pending: boolean
  label: string
  text: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={visible}
      aria-label={label}
      disabled={pending}
      onClick={onToggle}
      className="inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] px-1 text-sm font-medium text-[var(--color-text)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[32px]"
    >
      <span
        aria-hidden="true"
        className={`relative h-6 w-11 rounded-full transition-colors ${
          visible ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      >
        <span
          className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${
            visible ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </span>
      {text}
    </button>
  )
}
